import {MediaCardType} from '@wandb/weave/common/types/media';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {KeyTypes} from './common';
import {
  MediaBrowserPanelProps,
  MediaConfigWithDefaults,
  SizingSettings,
} from './Component';
import {PanelSettings} from './PanelSettings';

type PanelBodyProps = {
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  panelSettings?: React.ReactNode;
  configMode: boolean;
  panelViewerRef: React.MutableRefObject<HTMLDivElement | null>;
  mediaType: MediaCardType | null;
  sizingSettings: SizingSettings;
  configWithDefaults: MediaConfigWithDefaults;
  allMediaKeys: string[];
  keyTypes: KeyTypes;
  numericKeys: string[];
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  mediaBrowserRefCallback: (el: HTMLDivElement) => void;
};

// Create a panel body function so we can render our errors within
// the panel body and allow the panel to remain configurable
// on all error states
const PanelBodyComp = ({
  header,
  footer,
  children,
  configMode,
  panelViewerRef,
  mediaType,
  sizingSettings,
  configWithDefaults,
  allMediaKeys,
  keyTypes,
  numericKeys,
  updateConfig,
  mediaBrowserRefCallback,
}: PanelBodyProps) => {
  // FIXME: the sum below is the exact height of the height of the media panel.
  // This is calculated by taking the screen height less all other elements on
  // the page.
  const content = (
    <div className="flex h-full flex-col">
      <div>{header}</div>
      <div
        className={twMerge(
          'h-full overflow-hidden',
          'flex flex-col',
          /**
           * 60px account selector header
           * [< md] 55px project header
           * 57px workspace header
           * 1px fullscreen panel border top
           * 12px fullscreen panel padding top
           * [>= md] 88px fullscreen panel header
           * [< md] 56px fullscreen panel header
           * 30px media panel title (24px + 6px margin bottom)
           * -- Media panel --
           * 12px fullscreen panel padding bottom
           * 1px fullscreen panel border bottom
           */
          // FIXME: The sidebar incorrectly stays horizontal at the start of
          // the medium breakpoint. This won't be the right value at 768px.
          'max-h-[calc(100vh-60px-55px-57px-1px-12px-88px-30px-12px-1px)]',
          'md:max-h-[calc(100vh-60px-57px-1px-12px-56px-30px-12px-1px)]'
        )}>
        <div
          className="panel-media shrink overflow-auto"
          ref={mediaBrowserRefCallback}>
          <div ref={panelViewerRef} className="panel-viewer">
            {children}
          </div>
        </div>
        <div className="shrink-0">{footer}</div>
      </div>
    </div>
  );

  if (configMode) {
    return (
      <Tailwind>
        <div className="panel-media__config-container">
          <div className="panel-media__config-content">{content}</div>
          <div className="panel-media__config-settings">
            <PanelSettings
              mediaType={mediaType}
              sizingSettings={sizingSettings}
              allMediaKeys={allMediaKeys}
              numericKeys={numericKeys}
              config={configWithDefaults}
              updateConfig={updateConfig}
              keyTypes={keyTypes}
            />
          </div>
        </div>
      </Tailwind>
    );
  }

  return (
    <div data-test="media-panel" className="panel-media__container">
      {content}
    </div>
  );
};

export const PanelBody = React.memo(PanelBodyComp);
